import { useMemo, useState } from "react";

import { IListingStatsByCategory, IListingStatsResponse } from "../../models/Part";
import classNames from "../../utils/classNames";
import LoadingPulse from "../shared/LoadingPulse";

type CategoryOrder = "oldest" | "issues";

interface ICategoriesToWorkProps {
  stats: IListingStatsResponse | undefined;
  isLoading: boolean;
  activeCategoryId: string | null;
  onShow: (categoryId: string | null, categoryName: string | null) => void;
}

const DEFAULT_ROWS = 6;

const percent = (value: number | null | undefined): string => (value == null ? "—" : `${Math.round(value)}%`);

/** The issue score a category is ranked by under "Most issues": the two signal rates, weighted by how much old stock sits in it. */
export function categoryIssueScore(row: IListingStatsByCategory): number {
  return ((row.missingPartNumbersPercent ?? 0) + (row.pricedAboveIntelligencePercent ?? 0)) * Math.max(1, row.firstListedOver180 ?? 0);
}

/**
 * The listing stats by category, ranked so the next category to work is at the top: by the oldest
 * first-listed age, or by how many of its listings carry an issue. Show narrows the queue to it.
 */
export default function CategoriesToWork({ stats, isLoading, activeCategoryId, onShow }: ICategoriesToWorkProps) {
  const [order, setOrder] = useState<CategoryOrder>("oldest");
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    const categories = (stats?.byCategory ?? []).filter((row) => row.category && row.totalActive > 0);
    const sorted = [...categories].sort((a, b) =>
      order === "oldest"
        ? (b.oldestFirstListedDays ?? 0) - (a.oldestFirstListedDays ?? 0) || (b.firstListedOver180 ?? 0) - (a.firstListedOver180 ?? 0)
        : categoryIssueScore(b) - categoryIssueScore(a) || (b.firstListedOver180 ?? 0) - (a.firstListedOver180 ?? 0)
    );
    return sorted;
  }, [stats, order]);

  const visible = showAll ? rows : rows.slice(0, DEFAULT_ROWS);

  const segment = (value: CategoryOrder, label: string) => (
    <button
      type="button"
      onClick={() => setOrder(value)}
      aria-pressed={order === value}
      className={classNames(
        "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors",
        order === value ? "bg-primary-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"
      )}
    >
      {label}
    </button>
  );

  return (
    <section aria-label="Categories to work" className="flex h-full min-w-0 flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900">Categories to work</h2>
        <div role="group" aria-label="Order categories" className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
          {segment("oldest", "Oldest first")}
          {segment("issues", "Most issues")}
        </div>
      </div>

      {isLoading && !stats ? (
        <div className="px-4 py-6">
          <LoadingPulse />
        </div>
      ) : rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">No active listings to work.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2">Category</th>
                <th className="whitespace-nowrap px-3 py-2 text-right">180d+</th>
                <th className="whitespace-nowrap px-3 py-2 text-right">Oldest</th>
                <th className="whitespace-nowrap px-3 py-2 text-right" title="Listings with neither an MPN nor an OE number">No part no.</th>
                <th className="whitespace-nowrap px-3 py-2 text-right" title="Listings priced more than 10% over the intelligent price">Above intel.</th>
                <th className="px-4 py-2 text-right">
                  <span className="sr-only">Show</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((row) => {
                const active = row.category.id === activeCategoryId;
                return (
                  <tr key={row.category.id} className={classNames(active ? "bg-primary-50" : "hover:bg-gray-50")}>
                    <td className="max-w-[16rem] truncate whitespace-nowrap px-4 py-2 font-medium text-gray-900" title={row.category.name}>
                      {row.category.name}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-800">{(row.firstListedOver180 ?? 0).toLocaleString("en-GB")}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700">
                      {row.oldestFirstListedDays != null ? `${row.oldestFirstListedDays}d` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-800">{percent(row.missingPartNumbersPercent)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-800">{percent(row.pricedAboveIntelligencePercent)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => (active ? onShow(null, null) : onShow(row.category.id, row.category.name))}
                        className={classNames(
                          "rounded-md border px-2.5 py-1 text-xs font-semibold transition",
                          active
                            ? "border-primary-600 bg-primary-600 text-white hover:bg-primary-700"
                            : "border-primary-200 bg-white text-primary-700 hover:bg-primary-50"
                        )}
                      >
                        {active ? "Showing" : "Show"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > DEFAULT_ROWS && (
        <button
          type="button"
          onClick={() => setShowAll((value) => !value)}
          className="mt-auto border-t border-gray-100 px-4 py-2 text-left text-xs font-medium text-primary-700 hover:bg-gray-50"
        >
          {showAll ? "Show fewer" : `Show all ${rows.length} categories`}
        </button>
      )}
    </section>
  );
}
